import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { useRestaurantDatabase } from './RestaurantDatabaseContext';

// All translations for the Restaurant Panel
const translations = {
  en: {
    // Navigation
    today: 'Today',
    bookings: 'Bookings',
    currentBookings: 'Guests',
    tables: 'Tables',
    calendar: 'Calendar',
    settings: 'Settings',

    // Dashboard
    todaysOverview: "Today's Overview",
    addBooking: 'Add Booking',
    totalBookings: 'Total Bookings',
    pending: 'Pending',
    currentlySeated: 'Currently Seated',
    expectedGuests: 'Expected Guests',
    nextArrivals: 'Next Arrivals',
    viewAll: 'View all',
    noArrivalsIn2Hours: 'No arrivals in next 2 hours',
    todaysSummary: "Today's Summary",
    confirmed: 'Confirmed',
    seated: 'Seated',
    noBookingsToday: 'No bookings today',
    bookingsAppearRealTime: 'When guests make reservations, they\'ll appear here in real-time',
    needsConfirmation: 'Needs confirmation',
    review: 'Review',

    // Calendar / Make reservation
    makeReservation: 'Make a reservation',
    fullName: 'Full name',
    yourNameHere: 'Your name here',
    phoneNumber: 'Phone number',
    dateToCome: 'Date to come',
    timeToCome: 'Time to come',
    numberOfPeople: 'Number of people',
    notes: 'Notes',
    specialRequests: 'Special requests, allergies...',
    chooseTable: 'Choose table',
    all: 'All',
    createBooking: 'Create Booking',

    // Bookings page
    bookingsTitle: 'Bookings',
    noBookingsFound: 'No bookings found',
    noBookingsForDate: 'No bookings for this date',
    tryAdjustingSearch: 'Try adjusting your search',
    noStatusBookingsForDate: 'No {status} bookings for this date',
    goToToday: 'Go to Today',
    booking: 'booking',
    bookingsCount: 'bookings',
    searchPlaceholder: 'Search by name, email, phone, code...',
    status: 'Status',
    tomorrow: 'Tomorrow',
    guests: 'guests',
    completed: 'Completed',
    cancelled: 'Cancelled',
    noShow: 'No Show',

    // Tables
    tablesTitle: 'Tables',
    active: 'active',
    addTable: 'Add Table',
    noTablesConfigured: 'No tables configured',
    addTablesToStart: 'Add tables to start managing reservations',
    addFirstTable: 'Add Your First Table',
    tableName: 'Table Name',
    minGuests: 'Min Guests',
    maxGuests: 'Max Guests',
    location: 'Location',
    cancel: 'Cancel',
    edit: 'Edit',
    saveChanges: 'Save Changes',
    editTable: 'Edit Table',

    // Tables - Floor Plan
    list: 'List',
    floorPlan: 'Floor Plan',
    configure: 'Configure',
    available: 'Available',
    booked: 'Booked',
    inactive: 'Inactive',
    editLayout: 'Edit Layout',
    currentlyBooked: 'Currently Booked',
    noTablesOnFloorPlan: 'No tables positioned on floor plan',
    configureFloorPlan: 'Configure Floor Plan',
    addElement: 'Add:',
    tableElement: 'Table',
    sofaElement: 'Sofa',
    barElement: 'Bar',
    wallElement: 'Wall',
    dividerElement: 'Divider',
    labelElement: 'Label',
    addBackgroundImage: 'Add Background Image',
    replaceBackground: 'Replace Background',
    uploading: 'Uploading...',
    done: 'Done',
    shape: 'Shape:',
    seats: 'seats',
    editDetails: 'Edit Details',
    lengthLabel: 'Length:',
    thicknessLabel: 'Thickness:',
    rotationLabel: 'Rotation:',
    textLabel: 'Text:',
    sizeLabel: 'Size:',
    colorLabel: 'Color:',
    clickToPlaceTable: 'Click a table below, then click on the floor plan to place it:',
    positionedOnFloorPlan: 'Positioned on floor plan',
    tableShape: 'Table Shape',
    removeFromFloorPlan: 'Remove from floor plan',
    partySize: 'Party size',
    clickToPlace: 'Click to place',
    clickToAdd: 'Click to add',
    selected: 'Selected:',
    tablesNotOnFloorPlan: 'Tables not on floor plan:',
    quickSeat: 'Quick Seat (Walk-in)',
    configureFloorPlanFirst: 'Configure floor plan first',
    noTablesPositioned: 'No tables positioned',

    // Table shapes
    shapes: {
      round: 'Round',
      square: 'Square',
      rectangle: 'Rectangle',
      oval: 'Oval',
      booth: 'Booth',
      bar: 'Bar'
    },

    // Settings
    settingsTitle: 'Settings',
    saved: 'Saved!',
    language: 'Language',
    selectLanguage: 'Select the interface language for this panel',
    bookingStatus: 'Booking Status',
    bookingStatusDesc: 'Enable or disable online bookings',
    operatingHours: 'Operating Hours',
    open: 'Open',
    to: 'to',
    bookingLimits: 'Booking Limits',
    maxGuestsPerBooking: 'Max Guests per Booking',
    slotDuration: 'Slot Duration (minutes)',
    minutes: 'minutes',
    advanceBooking: 'Advance Booking',
    daysInAdvance: 'Days in Advance',
    daysInAdvanceDesc: 'How far ahead guests can book',
    minHoursNotice: 'Minimum Hours Notice',
    minHoursNoticeDesc: 'Minimum hours before booking time',
    maxBookingsPerSlot: 'Max Bookings per Slot',
    bookingRequirements: 'Booking Requirements',
    allowSpecialRequests: 'Allow Special Requests',
    allowSpecialRequestsDesc: 'Let guests add notes or dietary requirements',
    requirePhoneLabel: 'Require Phone Number',
    requirePhoneDesc: 'Guests must provide a phone number',
    requireEmailLabel: 'Require Email',
    requireEmailDesc: 'Guests must provide an email address',
    cancellationPolicyTitle: 'Cancellation Policy',
    policyText: 'Policy Text',
    managerOnly: 'Only managers can edit settings. Contact your manager for changes.',
    failedToLoad: 'Failed to load settings',

    // Days
    days: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    },

    // Months
    months: {
      january: 'January',
      february: 'February',
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December'
    },

    // Table locations
    locations: {
      indoor: 'Indoor',
      outdoor: 'Outdoor',
      terrace: 'Terrace',
      privateRoom: 'Private Room',
      bar: 'Bar',
      lounge: 'Lounge'
    },

    // Booking card & actions
    room: 'Room',
    specialRequestsLabel: 'Special Requests',
    confirmationCode: 'Confirmation Code',
    confirm: 'Confirm',
    seatGuest: 'Seat Guest',
    complete: 'Complete',
    cancelBooking: 'Cancel Booking',
    cancelBookingConfirm: 'Are you sure you want to cancel this booking for',
    reasonOptional: 'Reason (optional)',
    enterCancellationReason: 'Enter cancellation reason...',
    keepBooking: 'Keep Booking',
    delete: 'Delete',
    deleteBooking: 'Delete Booking',
    deleteBookingConfirm: 'Are you sure you want to permanently delete this booking for',
    deleteWarning: 'This action cannot be undone.',
    pendingBookings: 'Pending Bookings',
    noPendingBookings: 'No pending bookings',
    guest: 'guest',

    // GDPR
    gdpr: {
      title: 'GDPR & Privacy',
      description: 'Manage guest data rights, consent, and retention policies',
      guestDataRights: 'Guest Data Rights',
      guestDataRightsDesc: 'Search for a guest to export their data or anonymize their personal information (GDPR Art. 15-17).',
      searchByEmail: 'Search by guest email',
      search: 'Search',
      export: 'Export',
      anonymize: 'Anonymize',
      anonymized: 'Anonymized',
      consent: 'Consent',
      confirmAnonymizeTitle: 'Confirm Anonymization',
      confirmAnonymizeDesc: 'This will permanently remove all personal data for this guest and update linked reservations. This action cannot be undone.',
      confirmAnonymize: 'Yes, Anonymize',
      consentManagement: 'Consent Management',
      consentMarketing: 'Marketing Communications',
      consentSms: 'SMS Notifications',
      consentEmail: 'Email Notifications',
      consentHealthData: 'Health Data (Allergens / Dietary)',
      healthDataWarning: 'Health data consent is withdrawn. Allergen and dietary restriction data has been cleared.',
      consentUpdated: 'Consent updated',
      dataRetention: 'Data Retention Policy',
      dataRetentionDesc: 'Automatically anonymize guest profiles that have been inactive past the retention period (GDPR Art. 5(1)(e)).',
      retentionPeriod: 'Retention Period (days)',
      runCleanup: 'Run Cleanup Now',
      guestsAnonymized: 'guests anonymized',
      moreRemaining: 'More records remaining — run again to continue.',
      noGuestsPastRetention: 'No guests past retention period',
      auditLog: 'Audit Log',
      auditLogDesc: 'Recent data access and modification events for GDPR accountability (Art. 5(2)).',
      viewRecentActivity: 'Recent Activity',
      noAuditEntries: 'No audit entries yet',
      noGuestFound: 'No guest found with that email',
      searchFailed: 'Search failed',
      exportFailed: 'Export failed',
      dataExported: 'Guest data exported',
      dataAnonymized: 'Guest data anonymized',
      anonymizeFailed: 'Anonymization failed',
      retentionFailed: 'Retention cleanup failed',
      consentFailed: 'Failed to update consent',
      retentionInfo: 'Guest data is automatically anonymized after the configured retention period of inactivity per GDPR retention policy.',
      cascadedBookings: 'linked bookings updated',
      action: 'Action',
      date: 'Date',
      details: 'Details',
      performedBy: 'By',
    },
    // Reservation parameters (Feature 1)
    reservationParameters: 'Reservation Parameters',
    reservationParametersDesc: 'Configure default booking duration and time buffers',
    defaultDuration: 'Default Duration',
    defaultDurationDesc: 'Standard length of a reservation',
    bufferTime: 'Buffer Time',
    bufferTimeDesc: 'Gap between consecutive bookings on same table',
    maxOnlinePartySize: 'Max Online Party Size',
    maxOnlinePartySizeDesc: 'Maximum party size for online bookings. Larger groups must contact the restaurant directly.',
    autoConfirm: 'Auto-Confirm Online Bookings',
    autoConfirmDesc: 'When disabled, online bookings arrive as pending and require manual confirmation',
    pendingAwaitingConfirmation: 'bookings awaiting confirmation',

    // Walk-in table blocking (Feature 2)
    tableAvailability: 'Table Availability',
    tableAvailabilityDesc: 'Configure which tables are reserved for walk-in guests',
    walkInOnly: 'Walk-in Only',
    blockForWalkIn: 'Block for walk-in',
    minWalkInTables: 'Minimum Walk-in Tables',
    minWalkInTablesDesc: 'System will not allow online booking if fewer than this many tables would remain available for walk-in guests',
    weekdayOverrides: 'Weekday Overrides',
    walkInBadge: 'Walk-in',
    noTablesBlocked: 'No tables blocked for walk-in',

    // Booking source + email (Feature 3)
    email: 'Email',
    emailOptional: 'Email (optional)',
    bookingSource: 'Booking Source',
    sourcePhone: 'Phone',
    sourceOnline: 'Online (widget)',
    sourceWalkin: 'Walk-in',
    sourceEmail: 'Email',
    sourceOther: 'Other',
    sourceAdmin: 'Staff',
    sourceAiConcierge: 'AI Concierge',

    // Guest tags + legend (Feature 4)
    statusLegend: 'Legend',
    guestType: 'Guest Type',
    occasion: 'Occasion',
    dietary: 'Dietary',
    specialNeeds: 'Special Needs',

    // Statistics (Feature 5)
    statistics: 'Statistics',
    periodToday: 'Today',
    periodThisWeek: 'This Week',
    periodThisMonth: 'This Month',
    periodLast30Days: 'Last 30 Days',
    totalGuests: 'Total Guests',
    avgPartySize: 'Avg Party Size',
    noShowRate: 'No-Show Rate',
    dailyBookings: 'Daily Bookings',
    popularHours: 'Popular Hours',
    topTables: 'Top Tables',
    sourceDistribution: 'Source Distribution',
    bookingCount: 'Bookings',
    noDataForPeriod: 'No data for selected period',

    // Dashboard improvements (Feature 6)
    yesterday: 'Yesterday',
    lastWeek: 'Last week',
    upcomingReservations: 'Upcoming Reservations (next 2h)',
    noUpcomingReservations: 'No upcoming reservations in the next 2 hours',

    // CSV export (Feature 7)
    exportCSV: 'Export CSV',

    // Floor plan zones (Feature 8)
    zones: 'Zones',
    zone: 'Zone',
    addZone: 'Add Zone',
    editZone: 'Edit Zone',
    deleteZone: 'Delete Zone',
    zoneName: 'Zone Name',
    zoneColor: 'Zone Color',
    defaultZone: 'Default Zone',
    assignToZone: 'Assign to Zone',
    cannotDeleteDefaultZone: 'Cannot delete the default zone',
    manageZones: 'Manage Zones',
    noZone: 'No zone',
    inside: 'Indoor',

    gdprSettings: {
      title: 'GDPR / RODO Settings',
      description: 'Configure privacy and consent settings for the booking form',
      enabled: 'Enable GDPR Features',
      enabledDesc: 'Show consent options and privacy information on the booking form',
      policyUrl: 'Privacy Policy URL',
      policyUrlPlaceholder: 'https://example.com/privacy-policy',
      contactEmail: 'GDPR Contact Email',
      contactEmailPlaceholder: 'privacy@restaurant.com',
      contactEmailDesc: 'Email for data subject access requests',
      policyText: 'Privacy Policy Summary',
      policyTextDesc: 'Short privacy notice shown on the booking form',
      retentionDays: 'Data Retention Period',
      retentionDaysDesc: 'Number of days to retain booking data',
      retentionDefault: 'Default: 730 days (2 years)',
      days: 'days',
      consentSettings: 'Booking Form Consents',
      marketingConsent: 'Marketing Consent',
      marketingConsentDesc: 'Show marketing consent checkbox on booking form',
      smsConsent: 'SMS Consent',
      smsConsentDesc: 'Show SMS consent checkbox on booking form',
      emailConsent: 'Email Consent',
      emailConsentDesc: 'Show email consent checkbox on booking form',
    },
    demoData: 'Demo Data',
    demoDataDesc: 'Fill with dummy tables, bookings and hours for testing',
  },
  pl: {
    // Navigation
    today: 'Dziś',
    bookings: 'Rezerwacje',
    currentBookings: 'Goście',
    tables: 'Stoliki',
    calendar: 'Kalendarz',
    settings: 'Ustawienia',

    // Dashboard
    todaysOverview: 'Dzisiejszy przegląd',
    addBooking: 'Dodaj rezerwację',
    totalBookings: 'Wszystkie rezerwacje',
    pending: 'Oczekujące',
    currentlySeated: 'Obecnie przy stoliku',
    expectedGuests: 'Oczekiwani goście',
    nextArrivals: 'Następni goście',
    viewAll: 'Zobacz wszystkie',
    noArrivalsIn2Hours: 'Brak gości w ciągu 2 godzin',
    todaysSummary: 'Podsumowanie dnia',
    confirmed: 'Potwierdzone',
    seated: 'Przy stoliku',
    noBookingsToday: 'Brak rezerwacji na dziś',
    bookingsAppearRealTime: 'Gdy goście dokonają rezerwacji, pojawią się tutaj w czasie rzeczywistym',
    needsConfirmation: 'Wymaga potwierdzenia',
    review: 'Przejrzyj',

    // Calendar / Make reservation
    makeReservation: 'Utwórz rezerwację',
    fullName: 'Imię i nazwisko',
    yourNameHere: 'Wpisz imię i nazwisko',
    phoneNumber: 'Numer telefonu',
    dateToCome: 'Data wizyty',
    timeToCome: 'Godzina wizyty',
    numberOfPeople: 'Liczba osób',
    notes: 'Uwagi',
    specialRequests: 'Specjalne życzenia, alergie...',
    chooseTable: 'Wybierz stolik',
    all: 'Wszystkie',
    createBooking: 'Utwórz rezerwację',

    // Bookings page
    bookingsTitle: 'Rezerwacje',
    noBookingsFound: 'Nie znaleziono rezerwacji',
    noBookingsForDate: 'Brak rezerwacji na ten dzień',
    tryAdjustingSearch: 'Spróbuj zmienić kryteria wyszukiwania',
    noStatusBookingsForDate: 'Brak rezerwacji {status} na ten dzień',
    goToToday: 'Przejdź do dziś',
    booking: 'rezerwacja',
    bookingsCount: 'rezerwacji',
    searchPlaceholder: 'Szukaj po imieniu, email, telefonie, kodzie...',
    status: 'Status',
    tomorrow: 'Jutro',
    guests: 'gości',
    completed: 'Zakończone',
    cancelled: 'Anulowane',
    noShow: 'Nieobecność',

    // Tables
    tablesTitle: 'Stoliki',
    active: 'aktywne',
    addTable: 'Dodaj stolik',
    noTablesConfigured: 'Brak skonfigurowanych stolików',
    addTablesToStart: 'Dodaj stoliki, aby zarządzać rezerwacjami',
    addFirstTable: 'Dodaj pierwszy stolik',
    tableName: 'Nazwa stolika',
    minGuests: 'Min. gości',
    maxGuests: 'Maks. gości',
    location: 'Lokalizacja',
    cancel: 'Anuluj',
    edit: 'Edytuj',
    saveChanges: 'Zapisz zmiany',
    editTable: 'Edytuj stolik',

    // Tables - Floor Plan
    list: 'Lista',
    floorPlan: 'Plan sali',
    configure: 'Konfiguruj',
    available: 'Dostępny',
    booked: 'Zajęty',
    inactive: 'Nieaktywny',
    editLayout: 'Edytuj układ',
    currentlyBooked: 'Aktualnie zajęty',
    noTablesOnFloorPlan: 'Brak stolików na planie sali',
    configureFloorPlan: 'Konfiguruj plan sali',
    addElement: 'Dodaj:',
    tableElement: 'Stolik',
    sofaElement: 'Sofa',
    barElement: 'Bar',
    wallElement: 'Ściana',
    dividerElement: 'Przegroda',
    labelElement: 'Etykieta',
    addBackgroundImage: 'Dodaj tło',
    replaceBackground: 'Zmień tło',
    uploading: 'Przesyłanie...',
    done: 'Gotowe',
    shape: 'Kształt:',
    seats: 'miejsc',
    editDetails: 'Edytuj szczegóły',
    lengthLabel: 'Długość:',
    thicknessLabel: 'Grubość:',
    rotationLabel: 'Obrót:',
    textLabel: 'Tekst:',
    sizeLabel: 'Rozmiar:',
    colorLabel: 'Kolor:',
    clickToPlaceTable: 'Kliknij stolik poniżej, a następnie kliknij na plan sali, aby go umieścić:',
    positionedOnFloorPlan: 'Umieszczony na planie sali',
    tableShape: 'Kształt stolika',
    removeFromFloorPlan: 'Usuń z planu sali',
    partySize: 'Liczba osób',
    clickToPlace: 'Kliknij, aby umieścić',
    clickToAdd: 'Kliknij, aby dodać',
    selected: 'Wybrany:',
    tablesNotOnFloorPlan: 'Stoliki poza planem sali:',
    quickSeat: 'Szybkie posadzenie (Walk-in)',
    configureFloorPlanFirst: 'Najpierw skonfiguruj plan sali',
    noTablesPositioned: 'Brak stolików',

    // Table shapes
    shapes: {
      round: 'Okrągły',
      square: 'Kwadratowy',
      rectangle: 'Prostokątny',
      oval: 'Owalny',
      booth: 'Loża',
      bar: 'Bar'
    },

    // Settings
    settingsTitle: 'Ustawienia',
    saved: 'Zapisano!',
    language: 'Język',
    selectLanguage: 'Wybierz język interfejsu dla tego panelu',
    bookingStatus: 'Status rezerwacji',
    bookingStatusDesc: 'Włącz lub wyłącz rezerwacje online',
    operatingHours: 'Godziny otwarcia',
    open: 'Otwarte',
    to: 'do',
    bookingLimits: 'Limity rezerwacji',
    maxGuestsPerBooking: 'Maks. gości na rezerwację',
    slotDuration: 'Czas trwania (minuty)',
    minutes: 'minut',
    advanceBooking: 'Rezerwacja z wyprzedzeniem',
    daysInAdvance: 'Dni z wyprzedzeniem',
    daysInAdvanceDesc: 'Jak daleko z wyprzedzeniem goście mogą rezerwować',
    minHoursNotice: 'Minimalne wyprzedzenie (godziny)',
    minHoursNoticeDesc: 'Minimalna liczba godzin przed rezerwacją',
    maxBookingsPerSlot: 'Maks. rezerwacji na slot',
    bookingRequirements: 'Wymagania rezerwacji',
    allowSpecialRequests: 'Zezwól na specjalne życzenia',
    allowSpecialRequestsDesc: 'Goście mogą dodać uwagi lub wymagania dietetyczne',
    requirePhoneLabel: 'Wymagaj numeru telefonu',
    requirePhoneDesc: 'Goście muszą podać numer telefonu',
    requireEmailLabel: 'Wymagaj adresu email',
    requireEmailDesc: 'Goście muszą podać adres email',
    cancellationPolicyTitle: 'Polityka anulowania',
    policyText: 'Tekst polityki',
    managerOnly: 'Tylko menedżerowie mogą edytować ustawienia. Skontaktuj się z menedżerem.',
    failedToLoad: 'Nie udało się załadować ustawień',

    // Days
    days: {
      monday: 'Poniedziałek',
      tuesday: 'Wtorek',
      wednesday: 'Środa',
      thursday: 'Czwartek',
      friday: 'Piątek',
      saturday: 'Sobota',
      sunday: 'Niedziela'
    },

    // Months
    months: {
      january: 'Styczeń',
      february: 'Luty',
      march: 'Marzec',
      april: 'Kwiecień',
      may: 'Maj',
      june: 'Czerwiec',
      july: 'Lipiec',
      august: 'Sierpień',
      september: 'Wrzesień',
      october: 'Październik',
      november: 'Listopad',
      december: 'Grudzień'
    },

    // Table locations
    locations: {
      indoor: 'Wewnątrz',
      outdoor: 'Na zewnątrz',
      terrace: 'Taras',
      privateRoom: 'Sala prywatna',
      bar: 'Bar',
      lounge: 'Salon'
    },

    // Booking card & actions
    room: 'Pokój',
    specialRequestsLabel: 'Specjalne życzenia',
    confirmationCode: 'Kod potwierdzenia',
    confirm: 'Potwierdź',
    seatGuest: 'Przyjmij gościa',
    complete: 'Zakończ',
    cancelBooking: 'Anuluj rezerwację',
    cancelBookingConfirm: 'Czy na pewno chcesz anulować rezerwację dla',
    reasonOptional: 'Powód (opcjonalnie)',
    enterCancellationReason: 'Wpisz powód anulowania...',
    keepBooking: 'Zachowaj rezerwację',
    delete: 'Usuń',
    deleteBooking: 'Usuń rezerwację',
    deleteBookingConfirm: 'Czy na pewno chcesz trwale usunąć rezerwację dla',
    deleteWarning: 'Tej czynności nie można cofnąć.',
    pendingBookings: 'Oczekujące rezerwacje',
    noPendingBookings: 'Brak oczekujących rezerwacji',
    guest: 'gość',

    // GDPR
    gdpr: {
      title: 'RODO i prywatność',
      description: 'Zarządzaj prawami danych gości, zgodami i politykami przechowywania',
      guestDataRights: 'Prawa danych gościa',
      guestDataRightsDesc: 'Wyszukaj gościa, aby wyeksportować jego dane lub zanonimizować dane osobowe (RODO art. 15-17).',
      searchByEmail: 'Wyszukaj gościa po email',
      search: 'Szukaj',
      export: 'Eksport',
      anonymize: 'Anonimizuj',
      anonymized: 'Zanonimizowany',
      consent: 'Zgody',
      confirmAnonymizeTitle: 'Potwierdź anonimizację',
      confirmAnonymizeDesc: 'Spowoduje to trwałe usunięcie wszystkich danych osobowych tego gościa i aktualizację powiązanych rezerwacji. Tej czynności nie można cofnąć.',
      confirmAnonymize: 'Tak, anonimizuj',
      consentManagement: 'Zarządzanie zgodami',
      consentMarketing: 'Komunikacja marketingowa',
      consentSms: 'Powiadomienia SMS',
      consentEmail: 'Powiadomienia email',
      consentHealthData: 'Dane zdrowotne (alergeny / dieta)',
      healthDataWarning: 'Zgoda na dane zdrowotne została wycofana. Dane o alergenach i ograniczeniach dietetycznych zostały usunięte.',
      consentUpdated: 'Zgoda zaktualizowana',
      dataRetention: 'Polityka przechowywania danych',
      dataRetentionDesc: 'Automatycznie anonimizuj profile gości, którzy byli nieaktywni po upływie okresu przechowywania (RODO art. 5(1)(e)).',
      retentionPeriod: 'Okres przechowywania (dni)',
      runCleanup: 'Uruchom czyszczenie',
      guestsAnonymized: 'gości zanonimizowanych',
      moreRemaining: 'Więcej rekordów pozostaje — uruchom ponownie, aby kontynuować.',
      noGuestsPastRetention: 'Brak gości po okresie przechowywania',
      auditLog: 'Dziennik audytu',
      auditLogDesc: 'Ostatnie zdarzenia dostępu i modyfikacji danych dla rozliczalności RODO (art. 5(2)).',
      viewRecentActivity: 'Ostatnia aktywność',
      noAuditEntries: 'Brak wpisów audytu',
      noGuestFound: 'Nie znaleziono gościa z tym adresem email',
      searchFailed: 'Wyszukiwanie nie powiodło się',
      exportFailed: 'Eksport nie powiódł się',
      dataExported: 'Dane gościa wyeksportowane',
      dataAnonymized: 'Dane gościa zanonimizowane',
      anonymizeFailed: 'Anonimizacja nie powiodła się',
      retentionFailed: 'Czyszczenie retencji nie powiodło się',
      consentFailed: 'Nie udało się zaktualizować zgody',
      retentionInfo: 'Dane gości są automatycznie anonimizowane po skonfigurowanym okresie nieaktywności zgodnie z polityką RODO.',
      cascadedBookings: 'powiązanych rezerwacji zaktualizowanych',
      action: 'Akcja',
      date: 'Data',
      details: 'Szczegóły',
      performedBy: 'Przez',
    },
    // Reservation parameters (Feature 1)
    reservationParameters: 'Parametry rezerwacji',
    reservationParametersDesc: 'Konfiguracja domyślnego czasu trwania rezerwacji i buforów',
    defaultDuration: 'Domyślny czas trwania',
    defaultDurationDesc: 'Standardowa długość rezerwacji',
    bufferTime: 'Bufor między rezerwacjami',
    bufferTimeDesc: 'Czas na przygotowanie stolika między rezerwacjami',
    maxOnlinePartySize: 'Maks. liczba gości online',
    maxOnlinePartySizeDesc: 'Maksymalna liczba gości na rezerwację online. Większe grupy muszą kontaktować się bezpośrednio z restauracją.',
    autoConfirm: 'Automatyczne potwierdzanie rezerwacji online',
    autoConfirmDesc: 'Gdy wyłączone, rezerwacje online wymagają ręcznego potwierdzenia',
    pendingAwaitingConfirmation: 'rezerwacji oczekuje na potwierdzenie',

    // Walk-in table blocking (Feature 2)
    tableAvailability: 'Dostępność stolików',
    tableAvailabilityDesc: 'Konfiguracja stolików zarezerwowanych dla gości walk-in',
    walkInOnly: 'Tylko walk-in',
    blockForWalkIn: 'Zablokuj dla walk-in',
    minWalkInTables: 'Minimalna liczba stolików walk-in',
    minWalkInTablesDesc: 'System nie pozwoli zarezerwować online jeśli zostałoby mniej wolnych stolików niż podana liczba',
    weekdayOverrides: 'Ustawienia na dzień tygodnia',
    walkInBadge: 'Walk-in',
    noTablesBlocked: 'Brak stolików zablokowanych dla walk-in',

    // Booking source + email (Feature 3)
    email: 'Email',
    emailOptional: 'Email (opcjonalnie)',
    bookingSource: 'Źródło rezerwacji',
    sourcePhone: 'Telefon',
    sourceOnline: 'Online (widget)',
    sourceWalkin: 'Walk-in',
    sourceEmail: 'Email',
    sourceOther: 'Inne',
    sourceAdmin: 'Personel',
    sourceAiConcierge: 'AI Concierge',

    // Guest tags + legend (Feature 4)
    statusLegend: 'Legenda',
    guestType: 'Typ gościa',
    occasion: 'Okazja',
    dietary: 'Dieta',
    specialNeeds: 'Specjalne potrzeby',

    // Statistics (Feature 5)
    statistics: 'Statystyki',
    periodToday: 'Dziś',
    periodThisWeek: 'Ten tydzień',
    periodThisMonth: 'Ten miesiąc',
    periodLast30Days: 'Ostatnie 30 dni',
    totalGuests: 'Łączna liczba gości',
    avgPartySize: 'Średnia wielkość grupy',
    noShowRate: 'Wskaźnik nieobecności',
    dailyBookings: 'Rezerwacje dzienne',
    popularHours: 'Popularne godziny',
    topTables: 'Najpopularniejsze stoliki',
    sourceDistribution: 'Źródła rezerwacji',
    bookingCount: 'Rezerwacji',
    noDataForPeriod: 'Brak danych dla wybranego okresu',

    // Dashboard improvements (Feature 6)
    yesterday: 'Wczoraj',
    lastWeek: 'Tydzień temu',
    upcomingReservations: 'Nadchodzące rezerwacje (następne 2h)',
    noUpcomingReservations: 'Brak nadchodzących rezerwacji w ciągu 2 godzin',

    // CSV export (Feature 7)
    exportCSV: 'Eksport CSV',

    // Floor plan zones (Feature 8)
    zones: 'Strefy',
    zone: 'Strefa',
    addZone: 'Dodaj strefę',
    editZone: 'Edytuj strefę',
    deleteZone: 'Usuń strefę',
    zoneName: 'Nazwa strefy',
    zoneColor: 'Kolor strefy',
    defaultZone: 'Domyślna strefa',
    assignToZone: 'Przypisz do strefy',
    cannotDeleteDefaultZone: 'Nie można usunąć domyślnej strefy',
    manageZones: 'Zarządzaj strefami',
    noZone: 'Brak strefy',
    inside: 'Wewnątrz',

    gdprSettings: {
      title: 'Ustawienia GDPR / RODO',
      description: 'Skonfiguruj ustawienia prywatności i zgód dla formularza rezerwacji',
      enabled: 'Włącz funkcje RODO',
      enabledDesc: 'Pokaż opcje zgody i informacje o prywatności w formularzu rezerwacji',
      policyUrl: 'URL polityki prywatności',
      policyUrlPlaceholder: 'https://example.com/polityka-prywatnosci',
      contactEmail: 'Email kontaktowy RODO',
      contactEmailPlaceholder: 'prywatnosc@restauracja.pl',
      contactEmailDesc: 'Email do wniosków o dostęp do danych',
      policyText: 'Podsumowanie polityki prywatności',
      policyTextDesc: 'Krótka informacja o prywatności wyświetlana w formularzu rezerwacji',
      retentionDays: 'Okres przechowywania danych',
      retentionDaysDesc: 'Liczba dni przechowywania danych rezerwacji',
      retentionDefault: 'Domyślnie: 730 dni (2 lata)',
      days: 'dni',
      consentSettings: 'Zgody w formularzu rezerwacji',
      marketingConsent: 'Zgoda marketingowa',
      marketingConsentDesc: 'Pokaż pole zgody marketingowej w formularzu rezerwacji',
      smsConsent: 'Zgoda na SMS',
      smsConsentDesc: 'Pokaż pole zgody na SMS w formularzu rezerwacji',
      emailConsent: 'Zgoda na email',
      emailConsentDesc: 'Pokaż pole zgody na email w formularzu rezerwacji',
    },
    demoData: 'Dane demo',
    demoDataDesc: 'Wypełnij przykładowymi stolikami, rezerwacjami i godzinami do testów',
  }
};

type Language = 'en' | 'pl';
type Translations = typeof translations.en;

interface TranslationContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const TranslationContext = createContext<TranslationContextType | null>(null);

export function RestaurantTranslationProvider({ children }: { children: ReactNode }) {
  const { database, hotelId, restaurantId } = useRestaurantDatabase();
  const [language, setLanguage] = useState<Language>('en');

  // Load language from Firestore and listen for changes
  useEffect(() => {
    if (!database || !hotelId || !restaurantId) return;

    const panelSettingsRef = doc(
      database,
      `hotels/${hotelId}/restaurant_panel_settings`,
      restaurantId
    );

    // Listen for real-time updates
    const unsubscribe = onSnapshot(panelSettingsRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.language && (data.language === 'en' || data.language === 'pl')) {
          setLanguage(data.language);
        }
      }
    });

    return () => unsubscribe();
  }, [database, hotelId, restaurantId]);

  const t = translations[language];

  return (
    <TranslationContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useRestaurantTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useRestaurantTranslation must be used within RestaurantTranslationProvider');
  }
  return context;
}
